import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { useTheme } from "styled-components/macro";
import type { ColumnDef } from "@tanstack/react-table";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import type { DeepMap, FieldError, UseFormMethods } from "react-hook-form";
import {
  GhostButtonXSmallV2,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
  TertiaryButtonMediumV2,
} from "../../../../components/Buttons/Buttons";
import {
  DeleteIcon,
  PlusIcon,
  CheckIcon,
  XIcon,
} from "../../../../components/Icons/Icons";
import { Table } from "../../../../components/Table/Table";
import {
  TextBaseSemibold,
  TextMdRegular,
  TextMdSemibold,
  TextSmMedium,
  TextXlSemibold,
} from "../../../../components/Typography/TypographyV2";
import { Modal } from "../../../../components/Modal/Modal";
import { ApprovalStage } from "../../../../types/types.Workflow";
import { useStoreState } from "../../../../util/util";
import type { OptionType, MethodsOfUseForm } from "../../../../types/types";
import {
  zodOptionalSelectBoxDefault,
  zodSelectBoxDefault,
} from "../../../../util/zod.util";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { AttributeSchema } from "../../../../types/types.PIM";
import { createFormFieldForCollection } from "../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import { InputWrapper } from "../../../../components/AttributeGroupFormGenerator/AttributeGroupFormGenerator";
import type { IRow } from "../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { transformSectionToStage } from "./utils";
import ReactTooltip from "react-tooltip";
import { Loader } from "../../../../components/Loader/Loader";
import { LoaderContainer } from "../../../../components/ProductsLayout/SharedProductsLayout";
import { spacingTokens } from "../../../../designTokens";

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_3xl")};
  padding: ${({ theme }) => theme.resolve("spacing_4xl")}
    ${({ theme }) => theme.resolve("spacing_3xl")};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const ApproversWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_3xl")};
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
`;

const MainTitle = styled(TextBaseSemibold)`
  margin: 0;
`;

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const StageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
`;

const SectionHeader = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  align-items: center;
  padding-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

const SectionTitle = styled(TextMdSemibold)`
  margin: 0;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: center;
  padding-top: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const AddButtonContent = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  align-items: center;
`;

const IconGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding-top: ${({ theme }) => theme.resolve("spacing_md")};

  > * {
    flex: 1;
  }
`;

const CellFieldWrapper = styled.div`
  ${InputWrapper} {
    margin-bottom: 0;
  }
`;

const InvisibleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GENERATED_UUID = "-generated";

export type ApproverRow = {
  id: string;
  edit?: boolean;
  [key: string]: string | undefined | boolean;
};

export type ApproversSection = {
  key: string;
  title: string;
  columns: AttributeSchema[];
  data: ApproverRow[];
  isCompleted: boolean;
};

export type FormValue = {
  [key: string]: string | OptionType<string> | undefined;
};

type ApproversProps = {
  sections: ApproversSection[];
  deletingApproverKey?: string | null;
  onClose: () => void;
  onSubmit: (approvers: ApprovalStage[]) => void;
};

type ApproverSectionProps = {
  sectionKey: string;
  title: string;
  columns: ColumnDef<ApproverRow>[];
  data: ApproverRow[];
  isCompleted: boolean;
  formRef: React.MutableRefObject<UseFormMethods<FormValue> | null>;
  rowInEdit: { section: string; rowId: string } | null;
  onAddRow: () => void;
  onSaveRow: (values: FormValue) => void;
  onError: (errors: DeepMap<FormValue, FieldError>) => void;
  testId: string;
};

function ApproverSection({
  sectionKey,
  title,
  columns,
  data,
  isCompleted,
  formRef,
  rowInEdit,
  onAddRow,
  onSaveRow,
  onError,
  testId,
}: ApproverSectionProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const formId = sectionKey.replace(/_/g, "-");
  const isEditing = rowInEdit?.section === sectionKey && formRef.current;

  return (
    <StageSection>
      <SectionHeader>
        <SectionTitle>{title}</SectionTitle>
      </SectionHeader>
      <TableContainer>
        <Table
          columns={columns}
          data={data}
          isLoading={false}
          error={undefined}
          enableColumnResize={false}
          showLeftBorder={false}
        />
        {isEditing && (
          <form
            noValidate
            onSubmit={formRef.current!.handleSubmit(onSaveRow, onError)}
            id={`${formId}-form-${rowInEdit!.rowId}`}
            style={{ display: "none" }}
          />
        )}
        <AddButtonContainer>
          <TertiaryButtonMediumV2
            onClick={onAddRow}
            testid={testId}
            disabled={!!rowInEdit || isCompleted}
            data-for={`add-approver-${sectionKey}`}
            data-tip={
              isCompleted ? t("Cannot add approver on completed stage") : ""
            }
          >
            <AddButtonContent>
              <PlusIcon
                width={20}
                height={20}
                fill={
                  !!rowInEdit || isCompleted
                    ? theme.resolve("color_icon_disabled")
                    : theme.resolve("color_button_text_tertiary_enabled")
                }
              />
              {!!rowInEdit || isCompleted ? (
                <TextSmMedium color="tertiary">{t("Add")}</TextSmMedium>
              ) : (
                <TextSmMedium color="primary">{t("Add")}</TextSmMedium>
              )}
            </AddButtonContent>
          </TertiaryButtonMediumV2>
          <ReactTooltip
            id={`add-approver-${sectionKey}`}
            effect="solid"
            padding={"10px"}
          />
        </AddButtonContainer>
      </TableContainer>
    </StageSection>
  );
}

type ManagedApproverSectionProps = {
  section: ApproversSection;
  rowInEdit: { section: string; rowId: string } | null;
  setRowInEdit: React.Dispatch<
    React.SetStateAction<{ section: string; rowId: string } | null>
  >;
  isSubmitting: boolean;
  deletingApproverKey: string | null;
  registerGetter: (key: string, getter: () => ApprovalStage) => void;
};

function ManagedApproverSection({
  section,
  rowInEdit,
  setRowInEdit,
  isSubmitting,
  deletingApproverKey,
  registerGetter,
}: ManagedApproverSectionProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { notifyError } = useNotifications();
  const { storefront_id, tenant_id } = useStoreState();
  const [data, setData] = useState<ApproverRow[]>(section.data);
  const formRef = useRef<UseFormMethods<FormValue> | null>(null);
  const errorRef = useRef<DeepMap<FormValue, FieldError> | null>(null);

  const getValidationSchema = useCallback(
    (columns: AttributeSchema[]) => {
      const validationSchemaObj: {
        [prop: string]:
          | z.ZodString
          | z.ZodSchema
          | z.ZodOptional<z.ZodString | z.ZodSchema>;
      } = {};

      columns.forEach((col) => {
        if (col.input_type === "user") {
          validationSchemaObj[col.id] = col.is_required
            ? z.object({
                value: z.string().min(1, t("This field is required")),
                label: z.string(),
              })
            : zodOptionalSelectBoxDefault.nullable();
        } else if (col.input_type === "single_select") {
          validationSchemaObj[col.id] = col.is_required
            ? zodSelectBoxDefault(t)
            : zodOptionalSelectBoxDefault.nullable();
        } else {
          validationSchemaObj[col.id] = col.is_required
            ? z.string().min(1, t("This field is required"))
            : z.string().optional();
        }
      });

      return z.object(validationSchemaObj);
    },
    [t]
  );

  const form = useForm<FormValue>({
    resolver: zodResolver(getValidationSchema(section.columns)),
  });

  if (formRef.current === null) {
    formRef.current = form;
  }

  useEffect(() => {
    setData(section.data);
    setRowInEdit((current) => {
      if (!current || current.section !== section.key) {
        return current;
      }
      const stillExists = section.data.some((row) => row.id === current.rowId);
      return stillExists ? current : null;
    });
  }, [section.data, section.key, setRowInEdit]);

  useEffect(() => {
    registerGetter(section.key, () =>
      transformSectionToStage(section.key, data, section.columns)
    );
  }, [registerGetter, section.key, section.title, section.columns, data]);

  const generateUUID = useCallback(() => {
    const lastId = data[data.length - 1]?.id;
    return !lastId
      ? `${Math.floor(Math.random() * 1234)}${GENERATED_UUID}`
      : lastId.includes(GENERATED_UUID)
      ? `${lastId}-${Math.floor(Math.random() * 1234)}`
      : `${lastId}${GENERATED_UUID}-${Math.floor(Math.random() * 1234)}`;
  }, [data]);

  const handleAddRow = useCallback(() => {
    const uuid = generateUUID();
    const newRow: ApproverRow = {
      id: uuid,
      edit: true,
    };
    setData((prev) => [...prev, newRow]);
    if (formRef.current) {
      formRef.current.clearErrors();
      formRef.current.reset();
    }
    errorRef.current = null;
    setRowInEdit({ section: section.key, rowId: uuid });
  }, [generateUUID, section.key, setRowInEdit]);

  const handleSaveRow = useCallback(
    (rowId: string, values: FormValue) => {
      if (!formRef.current) {
        return;
      }

      const modifiedValues: ApproverRow = { id: rowId };
      Object.entries(values).forEach(([key, value]) => {
        const column = section.columns.find((c) => c.id === key);
        if (value && typeof value === "object" && "value" in value) {
          modifiedValues[key] = value.value as string;
          if (column?.input_type === "user") {
            modifiedValues[`${key}_name`] = (value.label as string) || "";
          }
        } else {
          modifiedValues[key] = value as string;
        }
      });

      const userColumn = section.columns.find((c) => c.input_type === "user");
      const newApproverId = userColumn
        ? (modifiedValues[userColumn.id] as string | undefined)
        : undefined;
      if (
        userColumn &&
        newApproverId &&
        data.some(
          (row) => row.id !== rowId && row[userColumn.id] === newApproverId
        )
      ) {
        notifyError(t("This user is already added as an approver"));
        setData((prev) =>
          prev.map((row) => (row.id === rowId ? { ...row, edit: false } : row))
        );
        setRowInEdit(null);
        formRef.current.clearErrors();
        formRef.current.reset();
        errorRef.current = null;
        return;
      }

      setData((prev) =>
        prev.map((row) =>
          row.id === rowId ? { ...modifiedValues, edit: false } : row
        )
      );
      setRowInEdit(null);
      formRef.current.clearErrors();
      formRef.current.reset();
      errorRef.current = null;
    },
    [data, notifyError, section.columns, setRowInEdit, t]
  );

  const handleDeleteRow = useCallback((id: string) => {
    // Remove from local state only; single POST on Save sends full list to backend
    setData((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const handleCancelEdit = useCallback(
    (rowId: string) => {
      if (rowId.includes(GENERATED_UUID)) {
        setData((prev) => prev.filter((row) => row.id !== rowId));
      } else {
        setData((prev) =>
          prev.map((row) => (row.id === rowId ? { ...row, edit: false } : row))
        );
      }

      if (formRef.current) {
        formRef.current.clearErrors();
        formRef.current.reset();
      }
      errorRef.current = null;
      setRowInEdit(null);
    },
    [setRowInEdit]
  );

  const createFormField = useCallback(
    (
      col: AttributeSchema,
      value: string | undefined,
      currentForm: UseFormMethods<FormValue>,
      errors: DeepMap<FormValue, FieldError>,
      rowId: string
    ) => {
      const methodsOfUseForm: MethodsOfUseForm = {
        ...currentForm,
        errors,
      };

      const row: IRow = {
        uuid: rowId,
        edit: true,
        row_editable: true,
      };

      // single_select defaultValue in createFormFieldForCollection expects [{ name }].
      const fieldValue =
        col.input_type === "single_select" && value && value !== "-"
          ? [{ name: value }]
          : value || null;

      return (
        <CellFieldWrapper>
          {createFormFieldForCollection({
            col,
            value: fieldValue,
            row,
            methodsOfUseForm,
            errors,
            tenant_id,
            storefront_id,
            t,
            has_reference_entity_mapping: false,
            multiSelectRefs: {} as any,
            menuPortalTarget:
              typeof document !== "undefined" ? document.body : null,
            menuPortalZIndex: 900,
          })}
        </CellFieldWrapper>
      );
    },
    [tenant_id, storefront_id, t]
  );

  const columns = useMemo<ColumnDef<ApproverRow>[]>(() => {
    const formId = section.key.replace(/_/g, "-");

    const baseColumns: ColumnDef<ApproverRow>[] = section.columns.map(
      (col) => ({
        header: () => (
          <TextSmMedium>
            {col.display_name ? t([col.display_name]) : col.name}
          </TextSmMedium>
        ),
        accessorKey: col.id,
        cell: ({ row, getValue }) => {
          const isEditing =
            row.original.edit &&
            rowInEdit?.section === section.key &&
            rowInEdit?.rowId === row.original.id;
          const value = getValue() as string | undefined;

          const canEditListBackedSelect =
            col.input_type !== "single_select" || Boolean(col.list_id);

          if (isEditing && formRef.current && canEditListBackedSelect) {
            return createFormField(
              col,
              value,
              formRef.current,
              errorRef.current ?? {},
              row.original.id
            );
          }

          if (col.input_type === "user") {
            const displayName = row.original[`${col.id}_name`] as
              | string
              | undefined;
            return (
              <TextMdRegular>{displayName || value || "--"}</TextMdRegular>
            );
          }

          return <TextMdRegular>{value || "--"}</TextMdRegular>;
        },
      })
    );

    return [
      ...baseColumns,
      {
        header: "",
        accessorKey: "actions",
        enableSorting: false,
        cell: ({ row }) => {
          const isEditing =
            row.original.edit &&
            rowInEdit?.section === section.key &&
            rowInEdit?.rowId === row.original.id;

          if (isEditing) {
            return (
              <IconGroup>
                <InvisibleButton
                  disabled={isSubmitting}
                  form={`${formId}-form-${row.original.id}`}
                  type="submit"
                >
                  <CheckIcon fill={theme.activeToggleBG} />
                </InvisibleButton>
                <InvisibleButton
                  type="button"
                  onClick={() => handleCancelEdit(row.original.id)}
                >
                  <XIcon fill={theme.destructiveTextColor} />
                </InvisibleButton>
              </IconGroup>
            );
          }

          const rowDeletingKey = `${section.title}-${row.original.id}`;
          const isDeletingThisRow = deletingApproverKey === rowDeletingKey;

          return (
            <>
              <GhostButtonXSmallV2
                disabled={data.length <= 1 || section.isCompleted}
                onClick={() => handleDeleteRow(row.original.id)}
                data-for={`delete-approver-${section.key}-${row.original.id}`}
                data-tip={
                  data.length <= 1
                    ? t("At least one approver is needed")
                    : section.isCompleted
                    ? t("Cannot delete approvers on completed stage")
                    : ""
                }
              >
                {isDeletingThisRow ? (
                  <LoaderContainer
                    style={{ padding: spacingTokens.spacing_none }}
                  >
                    <Loader
                      isLoading={true}
                      omitContainer={true}
                      width={16}
                      height={16}
                    />
                  </LoaderContainer>
                ) : (
                  <DeleteIcon
                    width={20}
                    height={20}
                    fill={
                      data.length <= 1 || section.isCompleted
                        ? theme.disabledLinkColor
                        : theme.errorIconColor
                    }
                  />
                )}
              </GhostButtonXSmallV2>

              <ReactTooltip
                id={`delete-approver-${section.key}-${row.original.id}`}
                effect="solid"
                padding={"10px"}
              />
            </>
          );
        },
      },
    ];
  }, [
    createFormField,
    data.length,
    deletingApproverKey,
    handleCancelEdit,
    handleDeleteRow,
    isSubmitting,
    rowInEdit,
    section.columns,
    section.isCompleted,
    section.key,
    section.title,
    t,
    theme,
  ]);

  const handleFormError = useCallback(
    (errors: DeepMap<FormValue, FieldError>) => {
      errorRef.current = errors;
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        const firstError = errors[errorKeys[0]];
        if (firstError?.message) {
          notifyError(firstError.message);
        }
      }
    },
    [notifyError]
  );

  return (
    <ApproverSection
      sectionKey={section.key}
      title={section.title}
      columns={columns}
      data={data}
      isCompleted={section.isCompleted}
      formRef={formRef}
      rowInEdit={rowInEdit}
      onAddRow={handleAddRow}
      onSaveRow={(values: FormValue) => {
        if (rowInEdit) {
          handleSaveRow(rowInEdit.rowId, values);
        }
      }}
      onError={handleFormError}
      testId={`add-${section.key.replace(/_/g, "-")}-approver`}
    />
  );
}

export function Approvers({
  onSubmit,
  onClose,
  sections,
  deletingApproverKey = null,
}: ApproversProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { notifyError } = useNotifications();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowInEdit, setRowInEdit] =
    useState<{ section: string; rowId: string } | null>(null);
  const gettersRef = useRef<Record<string, () => ApprovalStage>>({});

  const registerGetter = useCallback(
    (key: string, getter: () => ApprovalStage) => {
      gettersRef.current[key] = getter;
    },
    []
  );

  const handleSubmit = () => {
    setIsSubmitting(true);
    try {
      const approvers: ApprovalStage[] = sections.map((section) => {
        const getter = gettersRef.current[section.key];
        if (!getter) {
          throw new Error("Approver section is not ready");
        }
        return getter();
      });
      onSubmit(approvers);
      onClose();
    } catch (error) {
      console.error("Failed to update approvers:", error);
      notifyError(t("Failed to update approvers"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={true}
      closeModal={onClose}
      overlay={true}
      modalWidth={theme.resolve("width_lg")}
    >
      <ModalHeader>
        <TextXlSemibold>{t("Approval Management")}</TextXlSemibold>
      </ModalHeader>
      <ModalContent>
        <ApproversWrapper>
          <MainTitle>{t("Approvers")}</MainTitle>
          <SectionsContainer>
            {sections.map((section) => (
              <ManagedApproverSection
                key={section.key}
                section={section}
                rowInEdit={rowInEdit}
                setRowInEdit={setRowInEdit}
                isSubmitting={isSubmitting}
                deletingApproverKey={deletingApproverKey}
                registerGetter={registerGetter}
              />
            ))}
          </SectionsContainer>
        </ApproversWrapper>

        <ModalActions>
          <TertiaryButtonLargeV2 onClick={onClose}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 onClick={handleSubmit} loading={isSubmitting}>
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ModalActions>
      </ModalContent>
    </Modal>
  );
}
